import React, { useState, useEffect } from "react";
import {
  Button,
  Grid,
  Paper,
  TextField,
  Card,
  CardContent,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  ListItemIcon,
  ListItemText,
  Box
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import { DatePicker } from "@mui/x-date-pickers";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { v4 as uuidv4 } from 'uuid';
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";

import FileUpload from "../File/FileUpload";
import FileItem from "../File/FileItem";
import FormHeader from "../Form/FormHeader";
import { agreementSchema } from "../../validations/AgreementValidation";
import { createAgreement, IAAgreement } from "../../services/agreementServices";
import { getSetting } from "../../services/settingServices";
import { handleApiError } from "../../utils/errorHandler";
import dayjs from "dayjs";
import { STATUS_OPTIONS_PRIVATE } from "../../constants/status";
import AgreementAnalyze from "./AgreementAnalyze";
import GetValidityChip from "./GetValidityChip";


export default function AgreementForm() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [proformaLink, setProformaLink] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiAgreements, setAiAgreements] = useState([]);
  const [selectedAgreementIndex, setSelectedAgreementIndex] = useState(null);
  const [loadingIA, setLoadingIA] = useState(false);
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page") || 1;
  const search = searchParams.get("search") || "";
  const status = searchParams.get("status") || "all";

  useEffect(() => {
    async function fetchSetting() {
      try {
        const data = await getSetting();
        setProformaLink(data.proforma_link);
      } catch (error) {
        console.error("Error cargando configuración:", error);
      }
    }
    fetchSetting();
  }, []);

  useEffect(() => {
    return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
  }, [files]);

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.__id !== fileId));
  };

  const addFiles = (newFiles) => {
    const filesWithIds = newFiles.map(f => ({ ...f, __id: uuidv4() }));
    setFiles(prev => [...prev, ...filesWithIds]);
  };

  const handleCancel = () => {
    navigate(`/agreements?page=${page}&search=${search}&status=${status}`);
  };

  const initialValues = {
    order_number: "",
    agreement_type: "",
    objective: "",
    entity: "",
    resolution_number: "",
    suscription_date: null,
    effective_date: null,
    signers: [{ id: uuidv4(), name: '', position: '' }],
    files: null,
    proponent_email: "",
    notification_unit: "",
    notification_amount: "",
  };

  const onSubmit = async (data) => {
    if (!data.files || data.files.length === 0) {
      toast.error("Debe haber al menos un archivo adjunto.");
      return;
    }
    try {
      await createAgreement(data);
      navigate(`/agreements?page=${page}&search=${search}&status=${status}&refresh=${Date.now()}`);
      toast.success("Se ha agregado el nuevo convenio");
    } catch (err) {
      if (!err?.response) {
        toast.error("No responde el servidor");
      } else {
        handleApiError(err, "Error al guardar los datos");
      }
    }
  };

  return (
    <Paper elevation={0} sx={{ padding: 5 }}>
      <FormHeader title="Cargar convenio" />
      {proformaLink && (
        <Button
          variant="outlined"
          sx={{ mt: 3 }}
          onClick={() => window.open(proformaLink, "_blank", "noopener,noreferrer")}
        >
          Ver ejemplo de Proforma
        </Button>
      )}

      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={agreementSchema}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {(props) => {
          const handleSummarize = async () => {
            if (!files.length) return toast.error("Debe seleccionar un archivo primero.");
            const file = files[0];
            const formData = new FormData();
            formData.append("file", file);

            try {
              setLoadingIA(true);
              const res = await IAAgreement(formData);
              setAiSummary(res.data.summary);
              const agreements = res.data.data.agreements || [];
              setAiAgreements(agreements);
              setSelectedAgreementIndex(null);
            } catch (error) {
              toast.error("Error generando resumen con IA");
            } finally {
              setLoadingIA(false);
            }
          };

          return (
            <Form>
              <Grid container direction="row" spacing={2} sx={{ mt: 2 }}>
                {/* Columna izquierda */}
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      {aiAgreements.length > 0 && (
                        <FormControl
                          fullWidth
                          variant="outlined"
                          size="small"
                          error={
                            !!props.errors.agreement_type &&
                            props.touched.agreement_type
                          }
                        >
                          <InputLabel id="select-agreement-label">
                            Elegir convenio IA
                          </InputLabel>
                          <Select
                            labelId="select-agreement-label"
                            name="agreement_ia"
                            value={selectedAgreementIndex ?? ""}
                            label="Elegir convenio IA"
                            onChange={(e) => {
                              const index = e.target.value;
                              setSelectedAgreementIndex(index);
                              const data = aiAgreements[index];

                              props.setFieldValue(
                                "objective",
                                data.objective || ""
                              );
                              props.setFieldValue("entity", data.entity || "");
                              props.setFieldValue(
                                "order_number",
                                data.order_number || ""
                              );
                              props.setFieldValue(
                                "agreement_type",
                                data.agreement_type || ""
                              );
                              props.setFieldValue(
                                "resolution_number",
                                data.resolution_number || ""
                              );
                              props.setFieldValue(
                                "suscription_date",
                                data.suscription_date
                                  ? dayjs(data.suscription_date)
                                  : null
                              );
                              props.setFieldValue(
                                "effective_date",
                                data.effective_date
                                  ? dayjs(data.effective_date)
                                  : null
                              );
                              props.setFieldValue(
                                "notification_amount",
                                data.notification_amount || ""
                              );
                              props.setFieldValue(
                                "notification_unit",
                                data.notification_unit || ""
                              );
                              props.setFieldValue(
                                "proponent_email",
                                data.proponent_email || ""
                              );
                              if (Array.isArray(data.signers)) {
                                props.setFieldValue(
                                  "signers",
                                  data.signers.map((s) => ({
                                    id: uuidv4(),
                                    name: s.name,
                                    position: s.position,
                                  }))
                                );
                              }
                            }}
                          >
                            {aiAgreements.map((agreement, index) => (
                              <MenuItem key={index} value={index}>
                                {agreement.agreement_type} -{" "}
                                {agreement.entity || "Sin entidad"} (
                                {agreement.order_number})
                              </MenuItem>
                            ))}
                          </Select>
                          <ErrorMessage
                            name="agreement_ia"
                            component="div"
                            style={{
                              color: "#d32f2f",
                              fontSize: "0.75rem",
                              marginTop: "4px",
                            }}
                          />
                        </FormControl>
                      )}
                    </Grid>
                   
                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="order_number"
                        label="Nº de orden en el archivo físico *"
                        size="small"
                        fullWidth
                        variant="outlined"
                        error={
                          !!props.errors.order_number &&
                          props.touched.order_number
                        }
                        helperText={<ErrorMessage name="order_number" />}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        error={
                          !!props.errors.agreement_type &&
                          props.touched.agreement_type
                        }
                      >
                        <InputLabel id="agreement_type_label">
                          Tipo de convenio *
                        </InputLabel>
                        <Select
                          labelId="agreement_type_label"
                          name="agreement_type"
                          value={props.values.agreement_type}
                          label="Tipo de convenio *"
                          size="small"
                          fullWidth
                          onChange={(e) =>
                            props.setFieldValue(
                              "agreement_type",
                              e.target.value
                            )
                          }
                          onBlur={props.handleBlur}
                        >
                          <MenuItem value="Marco">Marco</MenuItem>
                          <MenuItem value="Específico">Específico</MenuItem>
                          <MenuItem value="Adenda o Anexo">
                            Adenda o Anexo
                          </MenuItem>
                        </Select>
                        <ErrorMessage
                          name="agreement_type"
                          component="div"
                          style={{
                            color: "#d32f2f",
                            fontSize: "0.75rem",
                            marginTop: "4px",
                          }}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="objective"
                        label="Objetivo *"
                        size="small"
                        fullWidth
                        multiline
                        maxRows={4}
                        variant="outlined"
                        error={
                          !!props.errors.objective && props.touched.objective
                        }
                        helperText={<ErrorMessage name="objective" />}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="entity"
                        label="Institución Co-celebrante *"
                        size="small"
                        fullWidth
                        variant="outlined"
                        error={!!props.errors.entity && props.touched.entity}
                        helperText={<ErrorMessage name="entity" />}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="resolution_number"
                        label="Nº de resolución *"
                        size="small"
                        fullWidth
                        variant="outlined"
                        error={
                          !!props.errors.resolution_number &&
                          props.touched.resolution_number
                        }
                        helperText={<ErrorMessage name="resolution_number" />}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <DatePicker
                        value={props.values.suscription_date}
                        onChange={(value) =>
                          props.setFieldValue("suscription_date", value)
                        }
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            name: "suscription_date",
                            label: "Fecha de suscripción",
                            variant: "outlined",
                            error:
                              !!props.errors.suscription_date &&
                              props.touched.suscription_date,
                            helperText: (
                              <ErrorMessage name="suscription_date" />
                            ),
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <DatePicker
                        value={props.values.effective_date}
                        onChange={(value) =>
                          props.setFieldValue("effective_date", value)
                        }
                        format="DD/MM/YYYY"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            name: "effective_date",
                            label: "Fecha de vigencia *",
                            variant: "outlined",
                            error:
                              !!props.errors.effective_date &&
                              props.touched.effective_date,
                            helperText: <ErrorMessage name="effective_date" />,
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="notification_amount"
                        label="Notificar antes de *"
                        size="small"
                        fullWidth
                        type="number"
                        inputProps={{ min: 1 }}
                        variant="outlined"
                        error={
                          !!props.errors.notification_amount &&
                          props.touched.notification_amount
                        }
                        helperText={<ErrorMessage name="notification_amount" />}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        error={
                          !!props.errors.notification_unit &&
                          props.touched.notification_unit
                        }
                      >
                        <InputLabel id="notification_unit_label">
                          Unidad de tiempo *
                        </InputLabel>
                        <Select
                          labelId="notification_unit_label"
                          name="notification_unit"
                          value={props.values.notification_unit}
                          label="Unidad de tiempo *"
                          size="small"
                          onChange={(e) =>
                            props.setFieldValue(
                              "notification_unit",
                              e.target.value
                            )
                          }
                          onBlur={props.handleBlur}
                        >
                          <MenuItem value="days">Días</MenuItem>
                          <MenuItem value="weeks">Semanas</MenuItem>
                          <MenuItem value="months">Meses</MenuItem>
                        </Select>
                        <ErrorMessage
                          name="notification_unit"
                          component="div"
                          style={{
                            color: "#d32f2f",
                            fontSize: "0.75rem",
                            marginTop: "4px",
                          }}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                      <Field
                        as={TextField}
                        name="proponent_email"
                        label="Correo del proponente *"
                        size="small"
                        fullWidth
                        type="email"
                        variant="outlined"
                        error={
                          !!props.errors.proponent_email &&
                          props.touched.proponent_email
                        }
                        helperText={<ErrorMessage name="proponent_email" />}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <FieldArray name="signers">
                        {({ push, remove, form }) => {
                          const { signers } = form.values;
                          return (
                            <>
                              {signers.map((signer, index) => (
                                <Grid
                                  container
                                  spacing={2}
                                  alignItems="center"
                                  key={signer.id}
                                  sx={{ mb: 1 }}
                                >
                                  <Grid item xs={5}>
                                    <Field
                                      as={TextField}
                                      name={`signers[${index}].name`}
                                      label="Nombre del firmante *"
                                      size="small"
                                      fullWidth
                                      error={
                                        !!props.errors.signers?.[index]?.name &&
                                        props.touched.signers?.[index]?.name
                                      }
                                      helperText={
                                        <ErrorMessage
                                          name={`signers[${index}].name`}
                                        />
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={5}>
                                    <Field
                                      as={TextField}
                                      name={`signers[${index}].position`}
                                      label="Cargo del firmante *"
                                      size="small"
                                      fullWidth
                                      error={
                                        !!props.errors.signers?.[index]
                                          ?.position &&
                                        props.touched.signers?.[index]?.position
                                      }
                                      helperText={
                                        <ErrorMessage
                                          name={`signers[${index}].position`}
                                        />
                                      }
                                    />
                                  </Grid>
                                  <Grid
                                    item
                                    xs={2}
                                    sx={{ textAlign: "center" }}
                                  >
                                    {index > 0 && (
                                      <Tooltip title="Eliminar firmante">
                                        <IconButton
                                          color="error"
                                          size="small"
                                          onClick={() => remove(index)}
                                        >
                                          <RemoveCircleIcon />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Grid>
                                </Grid>
                              ))}
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddCircleIcon />}
                                onClick={() =>
                                  push({ id: uuidv4(), name: "", position: "" })
                                }
                              >
                                Agregar firmante
                              </Button>
                            </>
                          );
                        }}
                      </FieldArray>
                    </Grid>

                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <FileUpload
                        setFieldValue={props.setFieldValue}
                        setFiles={setFiles}
                        files={files}
                      />
                      {files && (
                        <FileItem
                          files={files}
                          removeFile={removeFile}
                          displayDeleteButton={true}
                        />
                      )}
                      <Button
                        variant="outlined"
                        onClick={handleSummarize}
                        disabled={!files.length || loadingIA}
                        sx={{ mt: 2 }}
                      >
                        {loadingIA ? "Generando..." : "Generar resumen con IA"}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Columna derecha */}
                <Grid item xs={12} md={4}>
                  {aiSummary && (
                    <Card sx={{ bgcolor: "#f9f9f9", borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Resumen IA
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "pre-line" }}
                        >
                          {aiSummary}
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </Grid>
              </Grid>

              <Grid
                container
                justifyContent="flex-end"
                spacing={2}
                sx={{ mt: 4 }}
              >
                <Grid item>
                  <Button variant="contained" type="submit" size="large">
                    Guardar
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    size="large"
                  >
                    Cancelar
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </Paper>
  );
}
